/**
 * Hub Guide (Sep 2026): one plain-language doc per app + a what's-new feed,
 * shared by the web app (Guide page, per-page drawer, ⌘K) and the `guideAsk`
 * callable (Gemini answers grounded in these docs only).
 *
 * Content lives in shared/guide/*.md and is compiled into
 * guideContent.generated.ts by shared/scripts/build-guide.mjs.
 */
import { z } from 'zod';
export { GUIDE_DOCS, WHATS_NEW } from './guideContent.generated.js';
export const GUIDE_GROUP_ORDER = ['Start', 'People', 'Work', 'Finance', 'Revenue & insights', 'Admin'];
/**
 * Server-side visibility. Tool docs need the tool (or app admin), admin docs
 * need admin. `manager` / `onboarding` docs are shown to everyone server-side
 * (their content isn't sensitive; the web filters them by the real nav).
 */
export function guideDocVisible(doc, v) {
    const a = doc.access;
    if (a === 'all' || a === 'manager' || a === 'onboarding')
        return true;
    if (a === 'admin')
        return v.isAdmin;
    if (a === 'hubAdmin')
        return v.isAdmin || v.isHubAdmin;
    if (a.startsWith('tool:'))
        return v.isAdmin || v.enabledTools.includes(a.slice(5));
    return false;
}
/** The doc for a pathname: the longest route prefix (so /credit-cards/new → credit-cards). */
export function guideDocForPath(docs, pathname) {
    let best = null;
    for (const d of docs) {
        const hit = d.route === '/' ? pathname === '/' : pathname === d.route || pathname.startsWith(`${d.route}/`);
        if (hit && (!best || d.route.length > best.route.length))
            best = d;
    }
    return best;
}
// ── Retrieval (word overlap, no embeddings) — used by guideAsk and the client search ──
const STOP = new Set('the a an and or of to in on for is are be can i my me we our you your it this that what how do does why when where who which with from at by as not no if'.split(' '));
export function guideTokens(s) {
    return s.toLowerCase().replace(/[^a-z0-9äöüéèà\s-]/g, ' ').split(/\s+/).filter((w) => w.length > 2 && !STOP.has(w));
}
/** Score a doc for a question: title/oneliner hits weigh most, body hits log-scaled. */
export function scoreGuideDoc(doc, q) {
    const words = guideTokens(q);
    if (words.length === 0)
        return 0;
    const head = `${doc.title} ${doc.oneliner}`.toLowerCase();
    const body = doc.body.toLowerCase();
    let s = 0;
    for (const w of words) {
        if (head.includes(w))
            s += 6;
        const n = body.split(w).length - 1;
        if (n > 0)
            s += Math.min(3, 1 + Math.log2(n));
    }
    if (q.toLowerCase().includes(doc.title.toLowerCase()))
        s += 12;
    return s;
}
/** Pick up to `max` docs for a question; the page's own doc always first. */
export function pickGuideDocs(docs, q, docId, max = 4) {
    const scored = docs
        .map((d) => ({ d, s: scoreGuideDoc(d, q) + (d.id === docId ? 100 : 0) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .slice(0, max)
        .map((x) => x.d);
    if (scored.length > 0)
        return scored;
    return docs.filter((d) => d.id === 'home' || d.id === 'guide').slice(0, max);
}
// ── guideAsk callable contract ──
export const GuideAskSchema = z.object({
    q: z.string().trim().min(3).max(500),
    docId: z.string().max(60).nullish(),
});
//# sourceMappingURL=guide.js.map